import React, { useState, useEffect } from 'react';
import {
  Typography, Paper, Box, TextField, Button,
  Avatar, Grid, IconButton, Divider,
} from '@mui/material';
import { PhotoCamera, Delete, ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useTheme as useAppTheme } from '../styles/Theme';
import '../styles/styles.css';

const Profile = ({ notificationsRef }) => {
  const navigate = useNavigate();
  const { darkMode } = useAppTheme();
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState({ username: '', name: '', avatarUrl: '' });
  const [formData, setFormData] = useState({ name: '', password: '', confirmPassword: '' });

  const messages = {
    passwordsMismatch: "Passwords don't match.",
    profileUpdateSuccess: 'Profile updated.',
    avatarUploadSuccess: 'Photo updated.',
    avatarRemoveSuccess: 'Photo removed.',
    serverError: 'Something went wrong. Try again.',
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/'); return; }

    const cachedName = localStorage.getItem('name') || '';
    const cachedUsername = localStorage.getItem('username') || '';
    const cachedAvatar = localStorage.getItem('avatar_url') || '';

    setUser({ username: cachedUsername, name: cachedName, avatarUrl: cachedAvatar });
    setFormData((prev) => ({ ...prev, name: cachedName }));
    setIsAuthenticated(true);

    api.getUser().then((data) => {
      setUser({ username: data.username, name: data.name || '', avatarUrl: data.avatar_url || '' });
      setFormData((prev) => ({ ...prev, name: data.name || '' }));
      localStorage.setItem('name', data.name || '');
      localStorage.setItem('avatar_url', data.avatar_url || '');
    }).catch(() => {});
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (formData.password && formData.password !== formData.confirmPassword) {
      notificationsRef.current.showSnackbar(messages.passwordsMismatch, 'error');
      setLoading(false);
      return;
    }

    try {
      const body = {};
      if (formData.name !== user.name) body.name = formData.name;
      if (formData.password) body.password = formData.password;

      if (Object.keys(body).length > 0) {
        await api.updateUser(body);
        if (body.name) {
          setUser((prev) => ({ ...prev, name: body.name }));
          localStorage.setItem('name', body.name);
        }
      }

      notificationsRef.current.showSnackbar(messages.profileUpdateSuccess, 'success');
      setFormData((prev) => ({ ...prev, password: '', confirmPassword: '' }));
    } catch {
      notificationsRef.current.showSnackbar(messages.serverError, 'error');
    }
    setLoading(false);
  };

  const handleUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      setUser((prev) => ({ ...prev, avatarUrl: reader.result }));
      try {
        await api.updateUser({ avatar_url: reader.result });
        localStorage.setItem('avatar_url', reader.result);
        notificationsRef.current.showSnackbar(messages.avatarUploadSuccess, 'success');
      } catch {
        notificationsRef.current.showSnackbar(messages.serverError, 'error');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRemove = async () => {
    try {
      await api.updateUser({ avatar_url: '' });
      setUser((prev) => ({ ...prev, avatarUrl: '' }));
      localStorage.setItem('avatar_url', '');
      notificationsRef.current.showSnackbar(messages.avatarRemoveSuccess, 'success');
    } catch {
      notificationsRef.current.showSnackbar(messages.serverError, 'error');
    }
  };

  if (!isAuthenticated) return null;

  return (
    <Box sx={{ minHeight: '100vh', width: '100vw', backgroundColor: 'background.default', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '16px', boxSizing: 'border-box' }}>
      <Box sx={{ width: '100%', maxWidth: '480px' }}>
        <IconButton onClick={() => navigate('/')} color="primary" className="backButton">
          <ArrowBack />
        </IconButton>
        <Paper elevation={0} className="paper">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <Avatar
              sx={{ width: 80, height: 80, fontSize: '2rem', mb: 1, bgcolor: darkMode ? '#5a5a5e' : '#0071e3' }}
              src={user.avatarUrl}
              alt={user.username}
            >
              {user.name ? user.name[0].toUpperCase() : user.username ? user.username[0].toUpperCase() : ''}
            </Avatar>
            <Typography variant="h6" fontWeight={700} letterSpacing="-0.3px" mt={1}>
              {user.name || user.username}
            </Typography>
            <Typography variant="body2" color="text.secondary">@{user.username}</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              <IconButton component="label" size="small" sx={{ color: darkMode ? '#aeaeb2' : '#0071e3' }}>
                <input hidden accept="image/*" type="file" onChange={handleUpload} />
                <PhotoCamera fontSize="small" />
              </IconButton>
              <IconButton onClick={handleRemove} size="small" sx={{ color: darkMode ? '#aeaeb2' : '#ff3b30' }}>
                <Delete fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth label="Full Name" name="name" value={formData.name} onChange={handleChange} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Username" value={user.username} disabled />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.8px', fontSize: '0.72rem', mb: 1 }}>
                  Change Password
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="New Password" name="password" type="password" value={formData.password} onChange={handleChange} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Confirm Password" name="confirmPassword" type="password" value={formData.confirmPassword} onChange={handleChange} />
              </Grid>
              <Grid item xs={12}>
                <Button type="submit" variant="contained" fullWidth disabled={loading}
                  sx={{
                    height: 44, borderRadius: '10px', textTransform: 'none', fontWeight: 500, boxShadow: 'none',
                  }}>
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default Profile;